import diff from 'fast-diff';
import { fromEvent, Observable } from 'rxjs';
import { share, tap } from 'rxjs/operators';
import { ElementSelection, ElementSelectionService, patchTextContent } from '../..';
import { getElementsIntersectingRange } from '../../helpers/dom/element';
import { findAncestor, removeAllChildren } from '../../helpers/dom/node';
import { isParagraphElement, isParagraphEmpty } from '../../helpers/dom/paragraph';
import { ignoreDuringCompositions } from '../../helpers/ignoreDuringCompositions';
import { createPointsWithSelection } from '../../helpers/selection';
import { expandWhitespace } from '../../helpers/textToHtml';
import { TextUnderCursor } from '../../helpers/textUnderCursor';
import { isOnlyCollapsibleWhitespace } from '../../helpers/whitespace';
import { createConnectingService } from '../base/connectingService';
import { ServiceLike } from '../base/service';
import { ElementService } from '../dom/elementService';
import { IndentService, NULL_INDENT_SERVICE } from '../text/indentService';
import { TextUnderCursorService } from '../text/textUnderCursorService';

export type HtmlSpaceIndentServiceOptions = {
  hasParagraphSpaceIndent?: boolean;
};

export function createHtmlSpaceIndentService(
  element$: Observable<Element | null>,
  selectionService: ElementSelectionService,
  textUnderCursorService: TextUnderCursorService,
  paragraphTextUnderCursorService: TextUnderCursorService,
  options: HtmlSpaceIndentServiceOptions,
  ...dependencies: ServiceLike[]
): IndentService {
  return createConnectingService(
    element$,
    () => NULL_INDENT_SERVICE,
    element =>
      new HtmlSpaceIndentService(
        element,
        selectionService,
        textUnderCursorService,
        paragraphTextUnderCursorService,
        options,
        ...dependencies,
      ),
  );
}

const INDENT_LENGTH = 4;

interface HtmlSpaceIndentServiceState extends TextUnderCursor {
  spacesLength: number;
  indentLevel: number;
  anchorParagraph: HTMLDivElement | null;
  selectedParagraphs?: HTMLDivElement[];
  // Whether the paragraph under the cursor can be outdented, when there is no cross-paragraph selection
  canOutdentParagraphUnderCursor?: boolean;
}

export class HtmlSpaceIndentService extends ElementService<Element> implements IndentService {
  constructor(
    element: Element,
    selectionService: ElementSelectionService,
    textUnderCursorService: TextUnderCursorService,
    paragraphTextUnderCursorService: TextUnderCursorService,
    options: HtmlSpaceIndentServiceOptions,
    ...dependencies: ServiceLike[]
  ) {
    super(
      element,
      // TODO: Make changes$ more fine-grained.
      textUnderCursorService,
      // Process dependent effects first.
      ...dependencies,
      {
        effects$: fromEvent<KeyboardEvent>(element, 'keydown').pipe(
          ignoreDuringCompositions(element),
          tap(event => {
            if (event.defaultPrevented) {
              return;
            }

            const { key } = event;
            switch (key) {
              case 'Tab':
                if (event.shiftKey) {
                  if (this.canOutdent) {
                    event.preventDefault();
                    this.outdent();
                  }
                } else if (this.canIndent) {
                  event.preventDefault();
                  this.indent();
                }
                break;

              case 'Backspace':
                if (this.getCanChangeIndentLevel(1, true)) {
                  event.preventDefault();
                  this.outdent();
                }
                break;

              default:
            }
          }),
          share(),
        ),
      },
    );

    this.selectionService = selectionService;
    this.textUnderCursorService = textUnderCursorService;
    this.paragraphTextUnderCursorService = paragraphTextUnderCursorService;

    this.hasParagraphSpaceIndent = options.hasParagraphSpaceIndent || false;
  }

  private readonly selectionService: ElementSelectionService;
  private readonly textUnderCursorService: TextUnderCursorService;
  private readonly paragraphTextUnderCursorService: TextUnderCursorService;
  private readonly hasParagraphSpaceIndent: boolean;

  private get state(): HtmlSpaceIndentServiceState | undefined {
    const { textUnderCursor } = this.textUnderCursorService;
    if (!textUnderCursor) {
      return undefined;
    }

    let canOutdentParagraphUnderCursor: boolean = false;
    let anchorParagraph: HTMLDivElement | null = null;

    if (this.hasParagraphSpaceIndent) {
      const { selection } = this.selectionService;
      if (!selection) {
        return undefined;
      }

      anchorParagraph = findAncestor(selection.anchorNode, isParagraphElement, this.element);

      canOutdentParagraphUnderCursor =
        anchorParagraph !== null && getParagraphSpaceIndentLevel(anchorParagraph) > 0;

      const selectedParagraphs = getHighlightedParagraphsFromSelection(this.element, selection);

      // If there are no selected paragraphs, fallback on default behavior
      if (selectedParagraphs) {
        const spacesLength = Math.max(...selectedParagraphs.map(getParagraphSpaceIndentLength));
        const indentLevel = Math.max(...selectedParagraphs.map(getParagraphSpaceIndentLevel));

        return {
          ...textUnderCursor,
          indentLevel,
          spacesLength,
          anchorParagraph,
          canOutdentParagraphUnderCursor,
          selectedParagraphs,
        };
      }
    }

    // We must have a collapsed range/cursor.
    const { text, anchorOffset, focusOffset } = textUnderCursor;
    if (anchorOffset !== focusOffset) {
      return undefined;
    }

    // We must have some non-whitespace text before the cursor.
    const textBeforeCursor = text.substring(0, focusOffset);
    if (!this.hasParagraphSpaceIndent && isOnlyCollapsibleWhitespace(textBeforeCursor)) {
      return undefined;
    }

    // Determine the space before the cursor.
    const spacesBeforeCursorMatch = text.substring(0, focusOffset).match(/[^\S\n\r\f]+$/);
    const spacesBeforeCursor = spacesBeforeCursorMatch ? spacesBeforeCursorMatch[0] : '';
    const spacesLength = spacesBeforeCursor.length;
    const indentLevel = Math.floor(spacesBeforeCursor.length / INDENT_LENGTH);

    return {
      ...textUnderCursor,
      spacesLength,
      indentLevel,
      anchorParagraph,
      canOutdentParagraphUnderCursor,
    };
  }

  get canIndent() {
    return this.getCanChangeIndentLevel(0);
  }

  get canOutdent() {
    return this.getCanChangeIndentLevel(1);
  }

  private getCanChangeIndentLevel(minimumIndentLevel: number, withBackspace = false): boolean {
    const { state } = this;
    if (!state) {
      return false;
    }

    if (this.hasParagraphSpaceIndent) {
      if (!withBackspace && state.canOutdentParagraphUnderCursor && minimumIndentLevel === 1) {
        return true;
      }

      // We don't want backspace to outdent paragraphs
      if (withBackspace && state.selectedParagraphs) {
        return false;
      }
    }

    return state.indentLevel >= minimumIndentLevel;
  }

  indent() {
    const { state } = this;

    if (!state) {
      return;
    }

    if (this.hasParagraphSpaceIndent) {
      if (state.selectedParagraphs && state.selectedParagraphs.length > 0) {
        HtmlSpaceIndentService.indentParagraphs(state.selectedParagraphs);
        return;
      }

      // Handle indenting a new paragraph without a text node
      const { selection } = this.selectionService;
      if (!selection) {
        return;
      }

      const paragraph = selection.anchorNode;

      if (isParagraphElement(paragraph)) {
        const textNode = paragraph.ownerDocument.createTextNode(expandWhitespace(' '.repeat(INDENT_LENGTH)));
        paragraph.prepend(textNode);
        this.selectionService.selectWithOffsetsInContainers({
          anchorNode: textNode,
          anchorOffset: INDENT_LENGTH,
          focusNode: textNode,
          focusOffset: INDENT_LENGTH,
        });
        return;
      }
    }

    const { text, focusOffset } = state;

    const beforeText = text.substring(0, focusOffset);
    const afterText = text.substring(focusOffset);

    const indentSpaceCount = this.getIndentSpaceCount();
    const indentSpaces = expandWhitespace(' '.repeat(indentSpaceCount));

    const newText = beforeText + indentSpaces + afterText;
    const newFocusOffset = focusOffset + indentSpaces.length;

    this.textUnderCursorService.setTextUnderCursor({
      text: newText,
      anchorOffset: newFocusOffset,
      focusOffset: newFocusOffset,
    });
  }

  outdent() {
    const { state } = this;
    if (!state) {
      return;
    }

    if (state.selectedParagraphs && state.selectedParagraphs.length > 1) {
      this.outdentParagraphs(state.selectedParagraphs);
      return;
    }

    const { text, focusOffset, spacesLength } = state;
    const trimLength = Math.min(spacesLength, INDENT_LENGTH);

    if (trimLength < INDENT_LENGTH && state.anchorParagraph && state.canOutdentParagraphUnderCursor) {
      this.outdentParagraphs([state.anchorParagraph]);
      return;
    }

    const beforeText = text.substring(0, focusOffset - trimLength);
    const afterText = text.substring(focusOffset);
    const newText = beforeText + afterText;
    const newFocusOffset = focusOffset - trimLength;

    this.textUnderCursorService.setTextUnderCursor({
      text: newText,
      anchorOffset: newFocusOffset,
      focusOffset: newFocusOffset,
    });
  }

  private getIndentSpaceCount() {
    const { textUnderCursor } = this.paragraphTextUnderCursorService;

    const offset = textUnderCursor.focusOffset;

    return Math.abs((offset % INDENT_LENGTH) - INDENT_LENGTH);
  }

  private static indentParagraphs(paragraphs: HTMLDivElement[]) {
    for (const paragraph of paragraphs) {
      paragraph.prepend(
        paragraph.ownerDocument.createTextNode(
          expandWhitespace(
            ' '.repeat(INDENT_LENGTH - (getParagraphSpaceIndentLength(paragraph) % INDENT_LENGTH)),
          ),
        ),
      );
    }
  }

  private outdentParagraphs(paragraphs: HTMLDivElement[]) {
    const { selection } = this.selectionService;

    if (!selection) {
      return;
    }

    const points = createPointsWithSelection(selection);

    paragraphs.forEach(outdentParagraph);

    this.selectionService.selectWithPoints(points);
  }
}

function getHighlightedParagraphsFromSelection(
  container: Element,
  selection: ElementSelection,
): HTMLDivElement[] | null {
  if (selection.isCollapsed) {
    return null;
  }

  if (selection.anchorNode === selection.focusNode) {
    const paragraph = findAncestor(selection.anchorNode, isParagraphElement, container);
    return paragraph ? [paragraph] : null;
  }

  return Array.from(getElementsIntersectingRange(container, selection.range, isParagraphElement));
}

function getParagraphSpaceIndentLength(paragraph: HTMLDivElement) {
  const spacesBeforeCursorMatch = (paragraph.textContent ?? '').match(/^\s+/);
  return spacesBeforeCursorMatch ? spacesBeforeCursorMatch[0].length : 0;
}

function getParagraphSpaceIndentLevel(paragraph: HTMLDivElement) {
  return getParagraphSpaceIndentLength(paragraph) / INDENT_LENGTH;
}

function outdentParagraph(paragraph: HTMLDivElement) {
  const spaceLength = Math.min(INDENT_LENGTH, getParagraphSpaceIndentLength(paragraph));
  patchTextContent(paragraph, [[diff.DELETE, ' '.repeat(spaceLength)]]);

  // We want to keep empty paragraph
  if (isParagraphEmpty(paragraph)) {
    removeAllChildren(paragraph);
    paragraph.appendChild(paragraph.ownerDocument.createElement('br'));
  }
}
